import { useParam } from "@/lib/useAba";
import { useEffect, useState } from "react";
import { FileDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import { ReportView } from "@/components/reports/ReportView";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { REPORT_GROUPS, reportLabel } from "@/components/reports/catalog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, todayISO, fmtDate } from "@/lib/api";
import { exportCSV, exportXLSX, exportPDF } from "@/lib/exporters";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function ReportMenu({ kind, setKind, groups }) {
  return (
    <>
      <nav className="hidden lg:flex flex-col gap-4 w-60 shrink-0" data-testid="report-menu">
        {groups.map((g) => (
          <div key={g.group}>
            <p className="eyebrow mb-1.5 px-3">{g.group}</p>
            {g.items.map((r) => (
              <button key={r.k} onClick={() => setKind(r.k)} data-testid={`report-kind-${r.k}`}
                className={cn("w-full text-left rounded-lg px-3 py-2 text-sm transition-colors", kind === r.k ? "bg-primary/15 text-primary font-semibold" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
                {r.label}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div className="lg:hidden">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-11" data-testid="report-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>{groups.map((g) => <SelectGroup key={g.group}><SelectLabel>{g.group}</SelectLabel>{g.items.map((r) => <SelectItem key={r.k} value={r.k}>{r.label}</SelectItem>)}</SelectGroup>)}</SelectContent>
        </Select>
      </div>
    </>
  );
}

export default function Reports() {
  const { session } = useAuth();
  const [kind, setKind] = useState("vendas-periodo");
  const want = useParam("relatorio", "");
  useEffect(() => { if (want) setKind(want); }, [want]);
  const [f, setF] = useState({ start: todayISO(29), end: todayISO(), user_id: "", store_id: "", payment_method: "" });
  const [opts, setOpts] = useState({ users: [], stores: [], methods: [] });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/reports/filters").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setBusy(true);
    const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
    api.get(`/reports/data/${kind}?${qs}`).then((r) => setReport(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  }, [kind, f]);

  const title = reportLabel(kind);
  const name = `${kind}_${f.start}_${f.end}`;
  const subtitle = [`${session.tenant.nome_fantasia}`, `Período: ${fmtDate(f.start + "T12:00")} a ${fmtDate(f.end + "T12:00")}`,
    f.user_id && `Usuário: ${opts.users.find((u) => u.id === f.user_id)?.name}`, f.store_id && `Unidade: ${opts.stores.find((s) => s.id === f.store_id)?.name}`,
    f.payment_method && `Pagamento: ${opts.methods.find((m) => m.id === f.payment_method)?.name}`].filter(Boolean).join(" · ");
  const ready = report?.kind === kind && !busy;
  const groups = opts.reports ? REPORT_GROUPS.map((g) => ({ ...g, items: g.items.filter((r) => opts.reports.includes(r.k)) })).filter((g) => g.items.length) : REPORT_GROUPS;
  useEffect(() => { if (opts.reports && opts.reports.length && !opts.reports.includes(kind)) setKind(opts.reports[0]); }, [opts.reports, kind]);

  return (
    <>
      <PageHeader eyebrow="Gestão" title="Relatórios" subtitle="Escolha o relatório, ajuste os filtros e exporte em PDF, Excel ou CSV." actions={<>
        <Button variant="outline" onClick={() => exportPDF(name, title, subtitle, report)} disabled={!ready} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        <Button variant="outline" onClick={() => exportXLSX(name, title, report)} disabled={!ready} data-testid="export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        <Button variant="outline" onClick={() => exportCSV(name, report)} disabled={!ready} data-testid="export-csv-btn"><FileDown className="h-4 w-4 mr-1" />CSV</Button>
      </>} />
      <div className="flex flex-col lg:flex-row gap-6">
        <ReportMenu kind={kind} setKind={setKind} groups={groups} />
        <div className="flex-1 min-w-0 space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-lg md:text-xl font-bold" data-testid="report-title">{title}</h2>
            {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <ReportFilters f={f} setF={setF} opts={opts} />
          {report?.kind === kind && <ReportView report={report} />}
        </div>
      </div>
    </>
  );
}
